/**
 * PlaybookCard — clickable card for the Playbook list page. Shows
 * playbook id, version, rule/exemplar/refuted counts, and the most
 * recent published_at timestamp. Click → /playbooks/{playbook_id}.
 */

import { Link } from 'react-router-dom';
import { BookOpen, Star, Ban, Calendar, Activity } from 'lucide-react';
import type { PlaybookListItem } from '@/services/api';

interface PlaybookCardProps {
  playbook: PlaybookListItem;
}

const formatDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
};

export function PlaybookCard({ playbook }: PlaybookCardProps) {
  const {
    playbook_id,
    version,
    rule_count,
    exemplar_count,
    refuted_count,
    manifest,
  } = playbook;
  const empty = rule_count === 0 && exemplar_count === 0;

  return (
    <Link
      to={`/playbooks/${encodeURIComponent(playbook_id)}`}
      className="group block rounded-xl border border-slate-200 bg-white p-5 transition hover:border-brand-teal hover:shadow-soft focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-brand-teal/10 p-2 text-brand-teal">
          <BookOpen className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-800 truncate">
            {playbook_id}
          </h3>
          <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
            version {version}
          </p>
        </div>
      </div>

      {empty ? (
        <p className="mt-4 text-xs text-slate-500 italic">
          No rules extracted yet — bundle is scaffolded but the next garden
          cycle will populate it.
        </p>
      ) : (
        <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
          <Stat
            icon={<Activity className="w-3.5 h-3.5" />}
            label="rules"
            value={rule_count}
          />
          <Stat
            icon={<Star className="w-3.5 h-3.5" />}
            label="exemplars"
            value={exemplar_count}
          />
          <Stat
            icon={<Ban className="w-3.5 h-3.5" />}
            label="refuted"
            value={refuted_count}
          />
        </div>
      )}

      <div className="mt-4 flex items-center gap-1.5 text-[11px] text-slate-500">
        <Calendar className="w-3 h-3" />
        published {formatDate(manifest.published_at)}
        <span className="mx-1.5 text-slate-300">·</span>
        {manifest.episodes_processed} episodes processed
      </div>
    </Link>
  );
}

interface StatProps {
  icon: React.ReactNode;
  label: string;
  value: number;
}

function Stat({ icon, label, value }: StatProps) {
  return (
    <div className="flex flex-col items-start gap-0.5 rounded-md border border-slate-100 bg-slate-50 px-2 py-1.5">
      <div className="flex items-center gap-1 text-slate-500">
        {icon}
        <span className="text-[10px] uppercase tracking-wide">{label}</span>
      </div>
      <span className="text-sm font-semibold tabular-nums text-slate-800">
        {value}
      </span>
    </div>
  );
}
